import React from "react";
import {
  Box,
  Button,
  Card,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { authStorageGet } from "../utils/authStorage";

const PageWrapper = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#F3F4F6",
  padding: theme.spacing(3),
  boxSizing: "border-box",
}));

const MainCard = styled(Card)(() => ({
  width: "100%",
  maxWidth: 430,
  borderRadius: 20,
  padding: "30px 24px 28px",
  boxShadow: "0 8px 24px rgba(16, 24, 40, 0.08), 0 2px 6px rgba(16, 24, 40, 0.04)",
  border: "1px solid #E6E7EC",
  textAlign: "center",
  background: "#FFFFFF",
}));

const LogoCircle = styled(Box)(() => ({
  width: 72,
  height: 72,
  borderRadius: "50%",
  background: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto 24px",
  boxShadow: "0 4px 16px rgba(117, 63, 255, 0.14)",
  border: "1px solid #E9DDFE",
}));

const VaraLogo = styled(Box)(() => ({
  width: 38,
  height: 38,
  borderRadius: 8,
  background: "linear-gradient(135deg, #190B46 0%, #4B1FA1 48%, #FF8A3D 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#FFFFFF",
  fontWeight: 700,
  fontSize: 10,
  letterSpacing: "0.8px",
}));

const HighlightCard = styled(Box)(() => ({
  background: "#F6F2FF",
  border: "1px solid #E3D3FF",
  borderRadius: 16,
  padding: "30px 18px 24px",
  marginBottom: 22,
}));

const IdCard = styled(Box)(() => ({
  width: 62,
  height: 72,
  borderRadius: 12,
  background: "#FFFFFF",
  border: "1px solid #D9DEE5",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 4px 10px rgba(16, 24, 40, 0.08)",
}));

const MiniLogoCircle = styled(Box)(() => ({
  width: 62,
  height: 72,
  borderRadius: "50%",
  background: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 4px 10px rgba(16, 24, 40, 0.08)",
}));

const ContinueButton = styled(Button)(() => ({
  width: "100%",
  height: 44,
  borderRadius: 10,
  textTransform: "none",
  fontSize: 15,
  fontWeight: 700,
  color: "#FFFFFF",
  background: "linear-gradient(90deg, #8A1CF2 0%, #A61EFF 55%, #8A1CF2 100%)",
  boxShadow: "none",
  "&:hover": {
    background: "linear-gradient(90deg, #7A17D9 0%, #9719E8 55%, #7A17D9 100%)",
    boxShadow: "none",
  },
}));

// const SkipButton = styled(Button)(() => ({
//   textTransform: "none",
//   fontSize: 14,
//   fontWeight: 600,
//   color: "#101828",
//   "&:hover": {
//     background: "transparent",
//   },
// }));

const FeatureItem = ({ text }) => (
  <Stack direction="row" spacing={1.5} alignItems="center">
    <TaskAltRoundedIcon
      sx={{
        fontSize: 18,
        color: "#A61EFF",
        backgroundColor: "#F2E8FF",
        borderRadius: "50%",
      }}
    />
    <Typography
      sx={{
        fontSize: 14,
        lineHeight: "20px",
        fontWeight: 500,
        color: "#101828",
      }}
    >
      {text}
    </Typography>
  </Stack>
);

const VaraVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userId =
    location.state?.userId ?? authStorageGet("signupUserId") ?? undefined;
  const role =
    location.state?.role ?? authStorageGet("signupRole") ?? undefined;

  const varaInformationState =
    userId || role
      ? {
          ...(userId ? { userId } : {}),
          ...(role ? { role } : {}),
        }
      : undefined;

  const onContinue = () => {
    navigate("/vara-information", { state: varaInformationState });
  };

  return (
    <PageWrapper>
      <MainCard>
        <LogoCircle>
          <VaraLogo>VARA</VaraLogo>
        </LogoCircle>

        <Typography
          sx={{
            fontSize: 18,
            fontWeight: 700,
            color: "#101828",
            mb: 1.75,
          }}
        >
          VARA Digital Asset Compliance
        </Typography>

        <Typography
          sx={{
            fontSize: 14,
            lineHeight: "24px",
            color: "#667085",
            mb: 3.5,
            maxWidth: 350,
            mx: "auto",
          }}
        >
          Complete your Virtual Asset Regulatory Authority (VARA)
          verification to trade tokenized assets on the platform.
        </Typography>

        <HighlightCard>
          <Stack
            direction="row"
            spacing={2.25}
            justifyContent="center"
            alignItems="center"
            mb={2.25}
          >
            <IdCard>
              <PersonOutlineRoundedIcon
                sx={{ fontSize: 34, color: "#98A2B3" }}
              />
            </IdCard>

            <MiniLogoCircle>
              <VaraLogo
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 2,
                  fontSize: 10,
                }}
              >
                VARA
              </VaraLogo>
            </MiniLogoCircle>
          </Stack>

          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 500,
              color: "#344054",
            }}
          >
            Individual + Digital Asset Trading
          </Typography>
        </HighlightCard>

        <Stack spacing={1.75} sx={{ textAlign: "left", mb: 3.25 }}>
          <FeatureItem text="Saudi-regulated digital asset compliance" />
          <FeatureItem text="Trade tokenized real estate & assets" />
          <FeatureItem text="Full AML/CTF compliance coverage" />
        </Stack>

        <ContinueButton onClick={onContinue}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <VerifiedUserOutlinedIcon sx={{ fontSize: 18 }} />
            <span>Start VARA Verification</span>
            <ArrowForwardIosRoundedIcon sx={{ fontSize: 14 }} />
          </Stack>
        </ContinueButton>

        {/* <SkipButton onClick={handleSkip} disableRipple sx={{ mt: 2 }}>
          Skip for now
        </SkipButton> */}
      </MainCard>
    </PageWrapper>
  );
};

export default VaraVerification;